import { useState } from 'react';
import Modal from 'react-modal';
import deleteIcon from '../../images/icons/delete-icon.png'

/**
 * @typedef {object} LinkedDeleteOption
 * @property {string} key - Stable identifier passed back to deleteItem when selected
 * @property {string} label - Checkbox label shown to the user
 * @property {number} count - How many linked items this option would affect (option is hidden when 0)
 * @property {boolean} [defaultChecked] - Whether the checkbox starts ticked (defaults to false)
 */

/**
 * Confirmation dialog for a destructive delete. Optionally offers the user
 * cascade choices (e.g. "also delete the linked indicators") via `linkedOptions`;
 * the selected option keys are passed to `deleteItem`.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-removed-prop-resurrects-default
 * `softDelete` defaults to false, i.e. the hard-delete wording. Sites that
 * soft-delete (ADR-008) must pass it, otherwise this modal promises the data is
 * gone forever while it is only flagged. Before renaming or removing either
 * optional prop, grep it across src/ and migrate every call site in the same commit.
 * `softDelete` promises the trash bin — only true if the collection is listed in
 * src/helpers/trash/trashCollections.js and its docs carry CompagnyID.
 * AI-CONTEXT: See docs/pitfalls.md#ui-soft-delete-promises-a-trash-it-is-not-in
 *
 * @param {object} props
 * @param {boolean} props.deleteModal - Whether the modal is open
 * @param {(open: boolean) => void} props.setDeleteModal - Setter to open/close the modal
 * @param {(selectedKeys: string[]) => void} props.deleteItem - Runs the delete; receives the selected linked-option keys ([] when none)
 * @param {string} props.deleteName - Name of the item being deleted, shown in the confirm text
 * @param {LinkedDeleteOption[]} [props.linkedOptions] - Optional cascade choices rendered as checkboxes
 * @param {boolean} [props.softDelete] - When true, use soft-delete wording (recoverable via the trash bin) instead of "cannot be undone"
 * @param {string} [props.linkedNote] - What ELSE this delete removes, in the caller's own words. Without it the
 *   modal says nothing about linked data: most deletes remove only the one item, and the old fixed sentence
 *   'alle gekoppelde gegevens worden ook verwijderd' was false there. Ignored when checkboxes are shown.
 * @param {string} [props.undoNote] - Replaces the line about undoing (trash bin / 'kan niet ongedaan') and drops
 *   'definitief', for deletes that are neither: unlinking an indicator from its effect keeps the question itself.
 * @param {boolean} [props.activeSync] - The item has an active synchronisation with a portfolio. Shows what
 *   actually happens: the link gets status 'Verwijderd door project' and the portfolio, which only reads paired
 *   synchronisations, stops receiving this item's data. The data itself is not deleted by that.
 */
const DeleteModal = ({deleteModal, setDeleteModal, deleteItem, deleteName, linkedOptions, softDelete = false, linkedNote, undoNote, activeSync = false}) => {

  Modal.setAppElement('#root');

  // Only options that would actually affect something are shown.
  const visibleOptions = (linkedOptions || []).filter((option) => option.count > 0)

  const [selectedKeys, setSelectedKeys] = useState(() =>
    visibleOptions.filter((option) => option.defaultChecked).map((option) => option.key)
  )

  const modalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

  const toggleKey = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const deleteHandler = () => {
    // Cascade-aware callers pass `linkedOptions` and receive the selected keys.
    // Legacy callers get the exact no-argument call they always had.
    if (linkedOptions) {
      deleteItem(selectedKeys)
    } else {
      deleteItem()
    }

    setDeleteModal(false)

  }

  return (
    <Modal
      isOpen={deleteModal}
      onRequestClose={() => setDeleteModal(false)}
      style={modalStyles}
      contentLabel={softDelete || undoNote ? 'Verwijderen' : 'Definitief verwijderen'}
      >
        <div id='delete-modal-container'>
          <div id='delete-modal-text-container'>
            <div id='delete-modal-title-container'>
              <img src={deleteIcon} alt="" />
              <h1>Verwijderen</h1>
            </div>
            <h2>
              Weet je zeker dat je <i>{deleteName}</i>{softDelete || undoNote ? '' : ' definitief'} wilt verwijderen?
            </h2>
            <div id='delete-modal-notice-container'>
              <h3>Let op!</h3>
              {/* Over gekoppelde gegevens alleen wat waar is: met vinkjes beslist de
                  gebruiker zelf, anders zegt de aanroeper wat er meegaat — of niets.
                  AI-CONTEXT: See docs/pitfalls.md#ui-soft-delete-promises-a-trash-it-is-not-in */}
              <ul>
                {visibleOptions.length > 0 ? (
                  <li>Gekoppelde gegevens gaan alleen mee als je ze hieronder aanvinkt.</li>
                ) : linkedNote ? (
                  <li>{linkedNote}</li>
                ) : null}
                <li>{undoNote || (softDelete
                  ? 'Je kunt dit zelf terugzetten via de prullenbak, onderaan het menu links.'
                  : 'Deze actie kan niet ongedaan worden gemaakt.')}</li>
              </ul>
            </div>
            {activeSync ? (
              <div className='delete-modal-sync-notice-container'>
                <h3>Synchronisatie actief!</h3>
                <ul>
                  <li>{deleteName} heeft een actieve synchronisatie.</li>
                  <li>Het portfolio ontvangt hiervan daarna geen gegevens meer; daar staat de koppeling op 'Verwijderd door project'.</li>
                  {softDelete ? (
                    <li>Zet je het terug via de prullenbak, dan blijft de koppeling met het portfolio verbroken.</li>
                  ) : null}
                </ul>
              </div>
            ) : null}
            {visibleOptions.length > 0 ? (
              <div id='delete-modal-linked-options'>
                {visibleOptions.map((option) => (
                  <label key={option.key} className='delete-modal-linked-option'>
                    <input
                      type='checkbox'
                      checked={selectedKeys.includes(option.key)}
                      onChange={() => toggleKey(option.key)}
                    />
                    <span>{option.label} ({option.count})</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
          <div id='delete-modal-button-container'>
            <button onClick={() => setDeleteModal(false)}>Annuleren</button>
            <button id='delete-modal-delete-button' onClick={deleteHandler} >Verwijderen</button>
          </div>
        </div>
    </Modal>
  )
}

export default DeleteModal
